import { Badge, Banner, Button, Dialog, Field, Input } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useState, useEffect } from 'react';
import { FEATURES, SHOWCASE_ORDER } from '../lib/planCatalog';
import { DataTable as Table } from '../components/ui/DataTable';
import { ICONS } from '../components/ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { useAppToast } from '../hooks/useAppToast';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { palette } from '../lib/palette';
import { AddonPricingCard } from '../components/AddonPricingCard';
import { SkeletonText } from '../components/ui/Skeleton';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  monthlyPrice: number;
  annualPrice: number;
  features: Record<string, boolean>;
  maxUsers: number;
  maxContacts: number;
  maxCampaigns: number;
  maxConnections: number;
  aiDailyRequests?: number | null;
  stripePriceId?: string;
  active: boolean;
  sortOrder: number;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  planId?: string;
  planStatus: string;
  plan?: Plan;
}

const PLAN_COLORS: Record<string, string> = {
  FREE: 'var(--text-faint)',
  STARTER: 'var(--link)',
  PRO: 'var(--brand-text)',
  ENTERPRISE: '#8B5CF6',
};

const PLAN_BADGE_COLOR: Record<string, 'blue' | 'green' | 'orange' | 'red' | 'neutral'> = {
  FREE: 'neutral',
  STARTER: 'blue',
  PRO: 'green',
  ENTERPRISE: 'orange',
};

// Recursos de plano reais (mesmas chaves de backend/src/lib/planAccess.ts), na ordem da vitrine
const DEFAULT_FEATURES: Record<string, string> = Object.fromEntries(SHOWCASE_ORDER.map((k) => [k, FEATURES[k].title]));

/** embedded: dentro da aba Planos da Plataforma (sem cabeçalho de página) */
export function PlansPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const toast = useAppToast();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [saving, setSaving] = useState(false);

  const [newPlan, setNewPlan] = useState({
    name: '',
    displayName: '',
    description: '',
    monthlyPrice: 0,
    annualPrice: 0,
    maxUsers: 5,
    maxContacts: 1000,
    maxCampaigns: 10,
    maxConnections: 2,
    aiDailyRequests: null as number | null,
    features: {} as Record<string, boolean>,
    sortOrder: 0,
  });

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const authenticatedFetch = useAuthenticatedFetch();

  useEffect(() => {
    loadPlans();
    if (isSuperAdmin) loadTenants();
  }, [isSuperAdmin]);

  const loadPlans = async () => {
    try {
      const r = await authenticatedFetch('/api/plans?all=1');
      if (r.ok) {
        const data = await r.json();
        setPlans(data.plans || []);
      }
    } catch {
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  // Espelha todos os planos no Stripe (produto + preço mensal em BRL)
  const [syncing, setSyncing] = useState(false);
  const syncStripe = async () => {
    setSyncing(true);
    try {
      const r = await authenticatedFetch('/api/plans/stripe-sync', { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Erro ao sincronizar');
      const failed = (data.results || []).filter((x: { ok: boolean }) => !x.ok);
      if (failed.length) toast.error(`Falhou: ${failed.map((f: { name: string; error: string }) => `${f.name} (${f.error})`).join(', ')}`);
      else toast.success('Planos sincronizados com o Stripe');
      loadPlans();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const loadTenants = async () => {
    try {
      const r = await authenticatedFetch('/api/tenants');
      if (r.ok) {
        const data = await r.json();
        setTenants(data.tenants || []);
      }
    } catch {
      // silencioso
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.name || !newPlan.displayName) {
      toast.error('Nome e nome de exibição são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const r = await authenticatedFetch('/api/plans', {
        method: 'POST',
        body: JSON.stringify(newPlan),
      });
      if (r.ok) {
        toast.success('Plano criado com sucesso');
        setShowCreateModal(false);
        loadPlans();
        setNewPlan({
          name: '', displayName: '', description: '', monthlyPrice: 0, annualPrice: 0,
          maxUsers: 5, maxContacts: 1000, maxCampaigns: 10, maxConnections: 2, aiDailyRequests: null,
          features: {}, sortOrder: 0,
        });
      } else {
        const err = await r.json();
        toast.error(err.error || 'Erro ao criar plano');
      }
    } catch {
      toast.error('Erro ao criar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedPlan || !selectedTenantId) return;
    setSaving(true);
    try {
      const r = await authenticatedFetch('/api/plans/assign', {
        method: 'POST',
        body: JSON.stringify({ tenantId: selectedTenantId, planId: selectedPlan.id, planStatus: 'ACTIVE' }),
      });
      if (r.ok) {
        toast.success(`Plano ${selectedPlan.displayName} atribuído com sucesso`);
        setShowAssignModal(false);
        loadTenants();
      } else {
        const err = await r.json();
        toast.error(err.error || 'Erro ao atribuir plano');
      }
    } catch {
      toast.error('Erro ao atribuir plano');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (key: string) => {
    setNewPlan(p => ({ ...p, features: { ...p.features, [key]: !p.features[key] } }));
  };

  if (!isSuperAdmin) {
    return (
      <div className="page-content">
        <Banner variant="alert" description={<>
          Apenas Super Admins podem gerenciar planos.
        </>} />
      </div>
    );
  }

  if (showCreateModal) {
    return (
      <FormPage
        backTo="/super-admin"
        backLabel="Plataforma"
        onBack={() => setShowCreateModal(false)}
        title={'Novo plano'}
        description="Defina limites e preços do plano."
        submitLabel={'Criar plano'}
        submitting={saving}
        onSubmit={handleCreatePlan}
        onCancel={() => setShowCreateModal(false)}
      >
        <FormSection title="Detalhes">


        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Código do plano (ex: PRO)">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, name: e.target.value.toUpperCase() }))} value={newPlan.name} />
            </Field>
            <Field label="Nome de exibição">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, displayName: e.target.value }))} value={newPlan.displayName} />
            </Field>
          </div>
          <Field label="Descrição">
            <Input onChange={(e) => setNewPlan(p => ({ ...p, description: e.target.value }))} value={newPlan.description} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Preço mensal (R$)">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, monthlyPrice: Number(e.target.value) }))} type="number" value={String(newPlan.monthlyPrice)} />
            </Field>
            <Field label="Preço anual (R$)">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, annualPrice: Number(e.target.value) }))} type="number" value={String(newPlan.annualPrice)} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Máx. usuários">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, maxUsers: Number(e.target.value) }))} type="number" value={String(newPlan.maxUsers)} />
            </Field>
            <Field label="Máx. contatos">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, maxContacts: Number(e.target.value) }))} type="number" value={String(newPlan.maxContacts)} />
            </Field>
            <Field label="Máx. campanhas interativas">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, maxCampaigns: Number(e.target.value) }))} type="number" value={String(newPlan.maxCampaigns)} />
            </Field>
            <Field label="Máx. conexões">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, maxConnections: Number(e.target.value) }))} type="number" value={String(newPlan.maxConnections)} />
            </Field>
            <Field label="Pedidos de IA por dia" description="Vazio = padrão da plataforma">
              <Input onChange={(e) => setNewPlan(p => ({ ...p, aiDailyRequests: e.target.value === '' ? null : Number(e.target.value) }))} type="number" value={newPlan.aiDailyRequests == null ? '' : String(newPlan.aiDailyRequests)} />
            </Field>
          </div>

          {/* Feature flags */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 8 }}>Recursos incluídos</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {Object.entries(DEFAULT_FEATURES).map(([key, label]) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    background: newPlan.features[key] ? 'rgba(0,163,92,0.08)' : 'var(--surface-2)',
                    border: `1px solid ${newPlan.features[key] ? 'var(--brand-text)' : 'var(--border)'}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: 'var(--text)',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!newPlan.features[key]}
                    onChange={() => toggleFeature(key)}
                    style={{ accentColor: 'var(--brand-text)' }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          </div>
        </FormSection>
      </FormPage>
    );
  }

  return (
    <div style={embedded ? undefined : { padding: '28px 40px' }}>
      {/* Header da página */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: embedded ? 'flex-end' : 'space-between', marginBottom: embedded ? 16 : 24 }}>
        {!embedded && (
          <div>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Planos & Precificação</h2>
            <div className="!text-[var(--text-muted)]">
              Gerencie os planos disponíveis e atribua às organizações
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" icon={ICONS.Refresh} loading={syncing} onClick={syncStripe}>
            Sincronizar com Stripe
          </Button>
          <Button variant="primary" icon={ICONS.Plus} onClick={() => setShowCreateModal(true)}>
            Novo Plano
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8"><SkeletonText lines={4} /></div>
      ) : (
        <>
          {/* Grade de Planos */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, marginBottom: 40 }}>
            {plans.length === 0 ? (
              <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '48px 0', color: palette.gray.base }}>
                <ICONS.CreditCard size={24} />
                <div style={{ color: palette.gray.dark1 }}>Nenhum plano cadastrado. Crie o primeiro plano.</div>
              </div>
            ) : plans.map(plan => (
              <div
                key={plan.id}
                style={{
                  background: 'var(--surface)',
                  border: `2px solid ${PLAN_COLORS[plan.name] || 'var(--border)'}`,
                  borderRadius: 8,
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Badge variant={PLAN_BADGE_COLOR[plan.name] || 'neutral'}>
                        {plan.name}
                      </Badge>
                      {plan.active === false && <Badge variant="neutral">Inativo</Badge>}
                      <Badge variant={plan.stripePriceId ? 'green' : 'orange'}>
                        {plan.stripePriceId ? 'No Stripe' : 'Fora do Stripe'}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold" style={{ marginTop: 8, fontSize: 18 }}>{plan.displayName}</h3>
                    {plan.description && (
                      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{plan.description}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
                      {Number(plan.monthlyPrice).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>/mês</div>
                  </div>
                </div>

                {/* Limites */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      { label: 'Usuários', value: plan.maxUsers },
                      { label: 'Contatos', value: plan.maxContacts.toLocaleString('pt-BR') },
                      { label: 'Campanhas interativas', value: plan.maxCampaigns },
                      { label: 'Conexões', value: plan.maxConnections },
                      { label: 'IA por dia', value: plan.aiDailyRequests != null ? plan.aiDailyRequests.toLocaleString('pt-BR') : 'padrão' },
                    ].map(item => (
                      <div key={item.label} style={{ background: 'var(--surface-2)', borderRadius: 4, padding: '6px 10px' }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{item.value}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                {plan.features && Object.keys(plan.features).length > 0 && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                      RECURSOS
                    </div>
                    {Object.entries(plan.features).filter(([, v]) => v).map(([key]) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text)', marginBottom: 3 }}>
                        <ICONS.Checkmark size={16} />
                        {DEFAULT_FEATURES[key] || key}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 'auto' }}>
                  <Button variant="secondary" onClick={() => { setSelectedPlan(plan); setShowAssignModal(true); }}>
                    Atribuir a Organização
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Tabela de organizações e seus planos */}
          {tenants.length > 0 && (
            <>
              <div style={{ marginBottom: 16 }}>
                <h4 className="text-base font-semibold">Organizações e planos ativos</h4>
                <div style={{ color: palette.gray.dark1, marginTop: 4 }}>
                  Visão geral de qual plano cada organização está usando
                </div>
              </div>
              <div style={{ background: 'var(--surface)', border: `1px solid ${palette.gray.light2}`, borderRadius: 6, overflow: 'hidden' }}>
                <Table
                  variant="borderless"
                  items={tenants}
                  columnDefinitions={[
                    {
                      id: 'organizacao',
                      header: 'Organização',
                      cell: (tenant: Tenant) => <span style={{ fontWeight: 500 }}>{tenant.name}</span>,
                    },
                    {
                      id: 'slug',
                      header: 'Slug',
                      cell: (tenant: Tenant) => <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-muted)' }}>{tenant.slug}</span>,
                    },
                    {
                      id: 'plano',
                      header: 'Plano',
                      cell: (tenant: Tenant) => (
                        tenant.plan ? (
                          <Badge variant={PLAN_BADGE_COLOR[tenant.plan.name] || 'neutral'}>
                            {tenant.plan.displayName}
                          </Badge>
                        ) : (
                          <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>Sem plano</span>
                        )
                      ),
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: (tenant: Tenant) => (
                        <Badge variant={tenant.planStatus === 'ACTIVE' ? 'green' : tenant.planStatus === 'TRIAL' ? 'orange' : 'red'}>
                          {tenant.planStatus}
                        </Badge>
                      ),
                    },
                    {
                      id: 'acoes',
                      header: '',
                      cell: (tenant: Tenant) => (
                        <Button variant="secondary" onClick={() => { setSelectedTenantId(tenant.id); setSelectedPlan(null); setShowAssignModal(true); }}>
                          Alterar plano
                        </Button>
                      ),
                    },
                  ]}
                  ariaLabels={{ tableLabel: 'Organizações e planos ativos', selectionGroupLabel: 'Organizações e planos ativos' }}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Preços dos add-ons e créditos das ferramentas */}
      <AddonPricingCard />

      {/* Modal: Criar Plano */}


      {/* Modal: Atribuir Plano */}
      <Dialog.Root open={showAssignModal} onOpenChange={(o: boolean) => { if (!o) (() => setShowAssignModal(false))(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Atribuir Plano a Organização</h3>}</Dialog.Title>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
              Organização
            </label>
            <select
              value={selectedTenantId}
              onChange={e => setSelectedTenantId(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid var(--border)',
                borderRadius: 4, fontSize: 14, color: 'var(--text)', background: 'var(--surface)',
              }}
            >
              <option value="">Selecione uma organização...</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
              Plano
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {plans.map(plan => (
                <label
                  key={plan.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    border: `2px solid ${selectedPlan?.id === plan.id ? (PLAN_COLORS[plan.name] || 'var(--brand-text)') : 'var(--border)'}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    background: selectedPlan?.id === plan.id ? 'rgba(0,163,92,0.05)' : 'var(--surface)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="radio"
                      name="plan"
                      checked={selectedPlan?.id === plan.id}
                      onChange={() => setSelectedPlan(plan)}
                      style={{ accentColor: PLAN_COLORS[plan.name] || 'var(--brand-text)' }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{plan.displayName}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{plan.maxUsers} usuários · {plan.maxContacts.toLocaleString('pt-BR')} contatos</div>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>
                    {Number(plan.monthlyPrice).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-faint)' }}>/mês</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setShowAssignModal(false)}>Cancelar</Button>
            <Button variant="primary" loading={saving} disabled={!selectedPlan || !selectedTenantId} onClick={handleAssignPlan}>
              Atribuir Plano
            </Button>
          </div>
        </div>
      
</Dialog>
</Dialog.Root>
    </div>
  );
}
